import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Grid,
  Chip
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  warn: { color: "#d32f2f", marginTop: 8 }
}));

const TYPES = [
  "incident",
  "bug",
  "improvement",
  "support",
  "finance",
  "commercial",
  "implementation",
  "other"
];
const PRIORITIES = ["p1", "p2", "p3"];

const CardSchema = Yup.object().shape({
  title: Yup.string().min(2, "Muito curto").required("Obrigatório"),
  listId: Yup.string().required("Selecione uma lista")
});

const CreateTrelloCardModal = ({
  open,
  onClose,
  reload,
  ticketId,
  defaults = {}
}) => {
  const classes = useStyles();

  const [config, setConfig] = useState(null);
  const [lists, setLists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  const initialState = {
    title: defaults.title || "",
    type: "support",
    priority: "p3",
    listId: "",
    labelIds: [],
    due: "",
    summary: "",
    includeMessages: true,
    messageLimit: 10
  };

  useEffect(() => {
    if (!open) return;
    setDuplicateWarning(false);
    (async () => {
      setLoadingBoard(true);
      try {
        const { data: cfg } = await api.get(
          "/api/integrations/trello/config"
        );
        setConfig(cfg);
        if (cfg.configured && cfg.board_id) {
          const [listsRes, labelsRes] = await Promise.all([
            api.get(
              `/api/integrations/trello/boards/${cfg.board_id}/lists`
            ),
            api.get(
              `/api/integrations/trello/boards/${cfg.board_id}/labels`
            )
          ]);
          setLists(listsRes.data || []);
          setLabels(labelsRes.data || []);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingBoard(false);
      }
    })();
  }, [open]);

  const handleClose = () => {
    setDuplicateWarning(false);
    onClose();
  };

  const handleSave = async (values, actions) => {
    const payload = {
      ...values,
      due: values.due || null,
      confirmDuplicate: duplicateWarning
    };
    try {
      const { data } = await api.post(
        `/tickets/${ticketId}/trello-card`,
        payload
      );
      toast.success(i18n.t("trelloCards.toasts.created"));
      if (typeof reload === "function") reload();
      if (data?.card?.url) {
        window.open(data.card.url, "_blank", "noopener");
      }
      handleClose();
    } catch (err) {
      // 409 => já existe chamado para o ticket; pede confirmação e permite reenviar.
      if (err?.response?.status === 409) {
        setDuplicateWarning(true);
        toast.warn(i18n.t("trelloCards.toasts.duplicate"));
      } else {
        toastError(err);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  const notConfigured = config && !config.configured;
  const noBoard = config && config.configured && !config.board_id;
  const noLists =
    config && config.configured && config.board_id && !loadingBoard && !lists.length;

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{i18n.t("trelloCards.dialog.title")}</DialogTitle>
        <Formik
          initialValues={initialState}
          enableReinitialize
          validationSchema={CardSchema}
          onSubmit={(values, actions) => handleSave(values, actions)}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                {notConfigured && (
                  <Typography className={classes.warn} variant="body2">
                    {i18n.t("trelloCards.dialog.notConfigured")}
                  </Typography>
                )}
                {noBoard && (
                  <Typography className={classes.warn} variant="body2">
                    {i18n.t("trelloCards.dialog.noBoard")}
                  </Typography>
                )}
                {noLists && (
                  <Typography className={classes.warn} variant="body2">
                    {i18n.t("trelloCards.dialog.noLists")}
                  </Typography>
                )}

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      autoFocus
                      label={i18n.t("trelloCards.dialog.cardTitle")}
                      name="title"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>
                        {i18n.t("trelloCards.dialog.type")}
                      </InputLabel>
                      <Field
                        as={Select}
                        name="type"
                        label={i18n.t("trelloCards.dialog.type")}
                      >
                        {TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            {i18n.t(`trelloCards.types.${t}`)}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>
                        {i18n.t("trelloCards.dialog.priority")}
                      </InputLabel>
                      <Field
                        as={Select}
                        name="priority"
                        label={i18n.t("trelloCards.dialog.priority")}
                      >
                        {PRIORITIES.map((p) => (
                          <MenuItem key={p} value={p}>
                            {i18n.t(`trelloCards.priorities.${p}`)}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      type="date"
                      label={i18n.t("trelloCards.dialog.due")}
                      name="due"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>
                        {i18n.t("trelloCards.dialog.list")}
                      </InputLabel>
                      <Field
                        as={Select}
                        name="listId"
                        label={i18n.t("trelloCards.dialog.list")}
                        error={touched.listId && Boolean(errors.listId)}
                        disabled={loadingBoard || !lists.length}
                      >
                        {lists.map((l) => (
                          <MenuItem key={l.id} value={l.id}>
                            {l.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>
                        {i18n.t("trelloCards.dialog.labels")}
                      </InputLabel>
                      <Select
                        multiple
                        value={values.labelIds}
                        onChange={(e) =>
                          setFieldValue("labelIds", e.target.value)
                        }
                        label={i18n.t("trelloCards.dialog.labels")}
                        disabled={loadingBoard || !labels.length}
                        renderValue={(selected) => (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {selected.map((id) => {
                              const lab = labels.find((x) => x.id === id);
                              return (
                                <Chip
                                  key={id}
                                  size="small"
                                  label={lab ? lab.name || lab.color : id}
                                />
                              );
                            })}
                          </div>
                        )}
                      >
                        {labels.map((lab) => (
                          <MenuItem key={lab.id} value={lab.id}>
                            {lab.name || lab.color || lab.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("trelloCards.dialog.summary")}
                      name="summary"
                      multiline
                      rows={4}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.includeMessages}
                          onChange={(e) =>
                            setFieldValue("includeMessages", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={i18n.t("trelloCards.dialog.includeMessages")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      type="number"
                      label={i18n.t("trelloCards.dialog.messageLimit")}
                      name="messageLimit"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      disabled={!values.includeMessages}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 1, max: 50 }}
                    />
                  </Grid>
                </Grid>

                {duplicateWarning && (
                  <Typography className={classes.warn} variant="body2">
                    {i18n.t("trelloCards.dialog.duplicateConfirm")}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("trelloCards.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || notConfigured || noBoard || noLists}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {duplicateWarning
                    ? i18n.t("trelloCards.buttons.createAnyway")
                    : i18n.t("trelloCards.buttons.create")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CreateTrelloCardModal;
